/**
 * sidebarPref.js — whether the sidebar is a full column or an icon rail
 * =====================================================================
 * Usability review of 23 October 2026, item 1: *"Today every move between
 * pages means opening the hamburger menu, scrolling a long list and clicking.
 * On desktop, use a permanent left sidebar that can collapse to icons, with
 * the current page highlighted."* The reviewer's window was about 880px wide,
 * and the shell turned into a phone drawer below 900px, so a desktop reader
 * never saw the sidebar at all.
 *
 * The width decides the default and CSS applies it (Layout.css): a full
 * column above 1100px, an icon rail from 641 to 1100px, the drawer only on a
 * phone. The reader's own choice, once made, is remembered here and wins at
 * every width a sidebar is shown:
 *
 *   auto       the width decides
 *   collapsed  the rail, always
 *   expanded   the full column, always
 *
 * Rendering never reads the width — the first paint is the stylesheet's — so
 * the one place the width is read is a click, to know which way to toggle.
 */
const KEY = "pms.sidebar";

export const RAIL_MAX = 1100;
export const PHONE_MAX = 640;

export function readSidebarPref() {
  try {
    const value = window.localStorage.getItem(KEY);
    return value === "collapsed" || value === "expanded" ? value : "auto";
  } catch {
    return "auto";
  }
}

export function saveSidebarPref(pref) {
  try {
    if (pref === "auto") window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, pref);
  } catch {
    // A per-viewer convenience; nothing breaks without it.
  }
}

/** Whether the window is in the band where the rail is the default. */
export function isRailWidth(width = typeof window === "undefined" ? 1400 : window.innerWidth) {
  return width > PHONE_MAX && width <= RAIL_MAX;
}

/** The preference after one click of the collapse button. */
export function nextSidebarPref(pref, railWidth) {
  const showingRail = pref === "collapsed" || (pref === "auto" && railWidth);
  return showingRail ? "expanded" : "collapsed";
}

/** The class the shell carries for a preference. */
export const sidebarClass = (pref) =>
  pref === "collapsed" ? " sidebar-rail" : pref === "expanded" ? " sidebar-wide" : "";
